export type BomRule = 'fixed' | 'perimeter' | 'area' | 'led_length' | 'power_selector' | 'formula';

export interface BomCalcLine {
  id?: string;
  ruleType: BomRule | string;
  fixedQty: number | string;
  formula?: string | null;
  wastePct: number | string;
  isOptional?: boolean;
  optionCode?: string | null;
  unitKind?: string | null;
}

export interface BomCalcContext {
  widthMm: number;
  heightMm: number;
  powerW?: number | null;
  quantity?: number;
  configuration?: Record<string, unknown>;
}

type Vars = Record<string, number>;

/** Evaluador aritmético seguro (sin eval): + - * / paréntesis y variables. */
export function evaluateFormula(expression: string, vars: Vars): number {
  const src = expression.replace(/\s+/g, '');
  let pos = 0;

  const peek = () => src[pos];
  const parseExpression = (): number => {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = src[pos++];
      const right = parseTerm();
      value = op === '+' ? value + right : value - right;
    }
    return value;
  };
  const parseTerm = (): number => {
    let value = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const op = src[pos++];
      const right = parseFactor();
      value = op === '*' ? value * right : right === 0 ? 0 : value / right;
    }
    return value;
  };
  const parseFactor = (): number => {
    if (peek() === '+') { pos += 1; return parseFactor(); }
    if (peek() === '-') { pos += 1; return -parseFactor(); }
    if (peek() === '(') {
      pos += 1;
      const value = parseExpression();
      if (peek() === ')') pos += 1;
      return value;
    }
    if (/[0-9.]/.test(peek() ?? '')) {
      let raw = '';
      while (/[0-9.]/.test(peek() ?? '')) raw += src[pos++];
      const n = Number(raw);
      if (!Number.isFinite(n)) throw new Error(`Fórmula inválida: ${expression}`);
      return n;
    }
    if (/[a-zA-Z_]/.test(peek() ?? '')) {
      let name = '';
      while (/[a-zA-Z0-9_]/.test(peek() ?? '')) name += src[pos++];
      if (name in vars) return vars[name] ?? 0;
      // Constantes habituales
      if (name === 'PI') return Math.PI;
      throw new Error(`Variable desconocida "${name}" en fórmula "${expression}"`);
    }
    throw new Error(`Fórmula inválida: ${expression}`);
  };

  const result = parseExpression();
  if (pos !== src.length) throw new Error(`Fórmula inválida: ${expression}`);
  return result;
}

export function roundQty(value: number, decimals = 3): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Calcula la cantidad teórica de una línea de BOM.
 * - Las reglas geométricas usan mm de entrada y devuelven m / m².
 * - La merma se aplica siempre.
 * - Los artículos por unidades se redondean hacia arriba.
 */
export function computeBomQuantity(line: BomCalcLine, ctx: BomCalcContext): number {
  const config = ctx.configuration ?? {};
  if (line.isOptional && line.optionCode && !config[line.optionCode]) return 0;

  const width = Math.max(0, Number(ctx.widthMm) || 0) / 1000;
  const height = Math.max(0, Number(ctx.heightMm) || 0) / 1000;
  const vars: Vars = {
    width,
    height,
    perimeter: 2 * (width + height),
    area: width * height,
    power: Number(ctx.powerW) || 0,
    qty: ctx.quantity ?? 1
  };

  let base = 0;
  switch (line.ruleType) {
    case 'fixed':
      base = Number(line.fixedQty) || 0;
      break;
    case 'perimeter':
      base = vars.perimeter ?? 0;
      break;
    case 'area':
      base = vars.area ?? 0;
      break;
    case 'led_length':
      base = line.formula ? evaluateFormula(line.formula, vars) : vars.perimeter ?? 0;
      break;
    case 'power_selector':
      base = 1;
      break;
    case 'formula':
      base = evaluateFormula(line.formula || '0', vars);
      break;
    default:
      base = Number(line.fixedQty) || 0;
  }

  let total = base * (1 + (Number(line.wastePct) || 0) / 100) * (ctx.quantity ?? 1);
  if (line.unitKind === 'unit') total = Math.ceil(total - 1e-9);
  return roundQty(total);
}
